import { css } from 'lit';

/**
 * Shared component primitives.
 *
 * These are the pieces every page was previously re-inventing: 20 pages each
 * declared their own panel, their own row, their own input, their own empty
 * state — which is how the site ended up with 257 distinct hex colours and 159
 * font-family declarations.
 *
 * Import AFTER the tokens so the class rules can read them, and BEFORE the
 * page's own block so a page can still override:
 *
 *     static styles = [teknikTokens, teknikButtons, tkPrimitives, css`…`];
 *
 * Note on <tk-page>: its shell styles live in tk-page.ts, but content slotted
 * into it stays in the *host page's* shadow tree, so the host page is what must
 * import these primitives. That is deliberate — it keeps every page's markup
 * styled by rules it owns, with no ::slotted() gymnastics.
 */
export const tkPrimitives = css`
  /* ── Surfaces ─────────────────────────────────────────────────────────── */
  .tk-panel {
    background: var(--tk-panel);
    border: 1px solid var(--tk-border);
    border-radius: var(--tk-r-card);
  }

  .tk-panel-head {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.85rem 1.15rem;
    border-bottom: 1px solid var(--tk-border);
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--tk-dim);
  }

  .tk-panel-body { padding: 1.15rem; }

  /* A section marker: mono label with a hairline running to the right margin.
     The rule encodes structure, so don't use it for decoration. */
  .tk-rule {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 2.75rem 0 1.25rem;
  }
  .tk-rule > span {
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--tk-dim);
    white-space: nowrap;
  }
  .tk-rule::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--tk-border);
  }

  /* ── Rows ─────────────────────────────────────────────────────────────── */
  .tk-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.9rem 1.15rem;
    border: 1px solid var(--tk-border);
    border-radius: var(--tk-r-card);
    background: var(--tk-panel);
    min-width: 0;
    transition: border-color 0.18s ease, background 0.18s ease;
  }
  a.tk-row { text-decoration: none; color: inherit; }
  a.tk-row:hover, .tk-row.is-interactive:hover {
    border-color: var(--tk-border-strong);
    background: var(--tk-panel-2);
  }
  .tk-row-main { flex: 1; min-width: 0; }
  .tk-row-title { font-size: 0.95rem; font-weight: 600; color: var(--tk-text); }
  .tk-row-sub { margin-top: 0.2rem; font-size: 0.85rem; color: var(--tk-dim); line-height: 1.5; }

  /* ── Tiles (figures) ──────────────────────────────────────────────────── */
  .tk-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }
  .tk-tile {
    padding: 1.15rem;
    border-right: 1px solid var(--tk-border);
    min-width: 0;
  }
  .tk-tile:last-child { border-right: 0; }
  .tk-tile-n {
    font-family: var(--font-mono);
    font-size: 1.6rem;
    font-weight: 500;
    color: var(--tk-text);
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
    letter-spacing: -0.01em;
  }
  .tk-tile-l { margin-top: 0.35rem; font-size: 0.8125rem; color: var(--tk-dim); }

  /* ── Tags, badges, state ──────────────────────────────────────────────── */
  .tk-tag {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--tk-dim);
    border: 1px solid var(--tk-border);
    border-radius: 5px;
    padding: 3px 7px;
    white-space: nowrap;
  }

  /* Semantic only — these mean state, never brand. */
  .tk-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.04em;
    padding: 3px 8px;
    border-radius: 5px;
    background: var(--tk-panel-2);
    color: var(--tk-muted);
    white-space: nowrap;
  }
  .tk-badge.ok      { color: var(--tk-ok); }
  .tk-badge.warn    { color: var(--tk-warn); }
  .tk-badge.danger  { color: var(--tk-down); }

  .tk-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: var(--tk-dim);
  }
  .tk-dot.ok     { background: var(--tk-ok); box-shadow: 0 0 0 3px rgba(63, 185, 132, 0.15); }
  .tk-dot.warn   { background: var(--tk-warn); }
  .tk-dot.danger { background: var(--tk-down); }

  /* ── Feedback ─────────────────────────────────────────────────────────── */
  .tk-flash {
    padding: 0.7rem 0.95rem;
    border-radius: var(--tk-r-control);
    border: 1px solid var(--tk-border);
    background: var(--tk-panel-2);
    color: var(--tk-muted);
    font-size: 0.875rem;
  }
  .tk-flash.ok    { border-color: rgba(63, 185, 132, 0.35); color: var(--tk-ok); }
  .tk-flash.err   { border-color: rgba(239, 68, 68, 0.35); color: var(--tk-down); }

  /* An empty state should say what would fill it, not just "nothing here". */
  .tk-empty {
    padding: 2.5rem 1.15rem;
    text-align: center;
    color: var(--tk-dim);
    font-size: 0.9rem;
    line-height: 1.6;
  }

  @keyframes tk-pulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 0.9; } }
  @keyframes tk-spin { to { transform: rotate(360deg); } }

  .tk-skeleton {
    height: 1rem;
    border-radius: 4px;
    background: var(--tk-panel-2);
    animation: tk-pulse 1.4s ease-in-out infinite;
  }
  .tk-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid var(--tk-border);
    border-top-color: var(--tk-accent);
    border-radius: 50%;
    animation: tk-spin 0.6s linear infinite;
    display: inline-block;
  }

  /* ── Form controls ────────────────────────────────────────────────────── */
  .tk-input, .tk-select, .tk-textarea {
    width: 100%;
    box-sizing: border-box;
    background: var(--tk-panel-2);
    border: 1px solid var(--tk-border);
    border-radius: var(--tk-r-control);
    padding: 0.55rem 0.75rem;
    font-family: var(--font-family);
    font-size: 0.9rem;
    color: var(--tk-text);
    outline: none;
    transition: border-color 0.15s ease;
  }
  .tk-textarea { font-family: var(--font-mono); line-height: 1.6; resize: vertical; }
  .tk-input::placeholder, .tk-textarea::placeholder { color: var(--tk-dim); }
  .tk-input:focus, .tk-select:focus, .tk-textarea:focus { border-color: var(--tk-accent-line); }

  .tk-label {
    display: block;
    margin-bottom: 0.4rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--tk-muted);
  }
  .tk-hint { margin-top: 0.35rem; font-size: 0.78rem; color: var(--tk-dim); line-height: 1.5; }

  /* ── Tables ───────────────────────────────────────────────────────────── */
  /* Wide tables scroll inside their own container so the page body never
     scrolls sideways. */
  .tk-table-wrap { overflow-x: auto; border-radius: var(--tk-r-card); }
  .tk-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  .tk-table th {
    text-align: left;
    padding: 0.6rem 0.85rem;
    border-bottom: 1px solid var(--tk-border);
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--tk-dim);
    white-space: nowrap;
  }
  .tk-table td {
    padding: 0.6rem 0.85rem;
    border-bottom: 1px solid var(--tk-border);
    color: var(--tk-muted);
  }
  .tk-table tr:last-child td { border-bottom: 0; }
  .tk-table .num { font-family: var(--font-mono); font-variant-numeric: tabular-nums; text-align: right; }

  /* ── Utility ──────────────────────────────────────────────────────────── */
  .tk-mono { font-family: var(--font-mono); font-variant-numeric: tabular-nums; }
  .tk-muted { color: var(--tk-muted); }
  .tk-dim { color: var(--tk-dim); }
  .tk-stack { display: flex; flex-direction: column; gap: 10px; }
  .tk-cluster { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; min-width: 0; }

  @media (prefers-reduced-motion: reduce) {
    .tk-skeleton, .tk-spinner { animation: none; }
  }
`;
